import {
  CHILLDOC_MAX_BYTES,
  DOCUMENT_SHARE_SCOPES,
  type DocumentShareLinks,
  type DocumentShareScopeItem,
  readChilldoc,
  rejectedDoc,
  stripCommentAnchors,
  type ShareSnapshotRef,
  writeChilldoc,
} from '@apm/contracts';
import { fetchFileText } from '../preview/file-content';
import { myFilesApi } from '../api';
import { shareApi } from './share-api';
import { sha256Hex } from '../../../lib/sha256';

/**
 * Bản chụp đã lọc của link **giới hạn phạm vi** — APM-SHR-b, người dùng chốt 2026-09-29: trình
 * duyệt chủ tệp dựng rồi PUT presigned (luật #3 — nội dung không đi qua Node), link trỏ bản chụp.
 * Dựng từ **bản đã lưu** trên S3, không từ editor: bản chụp khai `sourceFileId` là hàng đã lưu, và
 * thay đổi chưa lưu trong editor không được lọt ra ngoài dưới tên của một bản đã lưu.
 */

/** Node bỏ đi khi phần tương ứng **không** có trong phạm vi. */
const DROPPED: Readonly<Record<Exclude<DocumentShareScopeItem, 'text'>, readonly string[]>> = {
  images: ['image', 'drawing'],
  // Biểu đồ dựng từ số của bảng — bỏ bảng mà giữ biểu đồ là vẫn lộ số.
  tables: ['table', 'chart'],
  presentation: ['slideBreak'],
};

function droppedTypes(scope: readonly DocumentShareScopeItem[]): Set<string> {
  return new Set(
    (Object.keys(DROPPED) as (keyof typeof DROPPED)[])
      .filter((part) => !scope.includes(part))
      .flatMap((part) => DROPPED[part]),
  );
}

/**
 * Bỏ node theo kiểu, **đệ quy** — cả node nằm trong thuộc tính (ô chữ của slide mang một `doc`
 * riêng). Chỉ lọc mảng `content` của ProseMirror: phần tử `type: 'image'` của hình vẽ Excalidraw
 * nằm ở `elements`, không phải node, và không được cắt nhầm.
 */
export function pruneNodes<T>(value: T, dropped: ReadonlySet<string>): T {
  if (Array.isArray(value)) return value.map((item) => pruneNodes(item, dropped)) as T;
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    out[key] =
      key === 'content' && Array.isArray(child)
        ? child
            .filter(
              (node) =>
                !(
                  node &&
                  typeof node === 'object' &&
                  dropped.has(String((node as { type?: unknown }).type))
                ),
            )
            .map((node) => pruneNodes(node, dropped))
        : pruneNodes(child, dropped);
  }
  return out as T;
}

/** Nội dung `.chilldoc` ⇒ bản đã lọc theo phạm vi. Ảnh nhúng không còn node nào trỏ tới thì rơi theo. */
export function filterChilldoc(text: string, scope: readonly DocumentShareScopeItem[]): string {
  const read = readChilldoc(text);
  if (!read.ok) throw new Error(`chilldoc ${read.reason}`);
  const dropped = droppedTypes(scope);
  // Bình luận là trao đổi nội bộ của người soạn — bản chụp cho người nhận không mang (APM-CMT-c).
  const { comments: _comments, ...envelope } = read.envelope;
  // Đề xuất chưa duyệt cũng là việc của người soạn: người nhận thấy bản như thể đã từ chối
  // (QA 2026-10-13, APM-CD2-qa3). Người có quyền "Đề xuất" đọc bản gốc, không phải bản này.
  const doc = rejectedDoc(stripCommentAnchors(pruneNodes(envelope.doc, dropped)));
  // Bỏ trình bày ⇒ bỏ luôn slide Mở đầu / Kết thúc ở vỏ.
  const presentation = scope.includes('presentation')
    ? pruneNodes(envelope.presentation, dropped)
    : undefined;
  return writeChilldoc({ ...envelope, doc, presentation }, read.images);
}

/** Dựng bản chụp từ bản đã lưu `sourceFileId`, tải lên, trả tham chiếu để gửi kèm lệnh tạo / cập nhật link. */
export async function uploadSnapshot(
  fileId: string,
  sourceFileId: string,
  scope: readonly DocumentShareScopeItem[],
): Promise<ShareSnapshotRef> {
  const filtered = filterChilldoc(await savedText(sourceFileId), scope);
  const body = new TextEncoder().encode(filtered);
  const key = await putSnapshot(fileId, body);
  return { key, sourceFileId };
}

async function savedText(fileId: string): Promise<string> {
  const { url } = await myFilesApi.downloadUrl(fileId);
  return fetchFileText(url, CHILLDOC_MAX_BYTES);
}

/** Xin chỗ và PUT thẳng S3 (luật #3) — trả key để gửi kèm lệnh. */
async function putSnapshot(fileId: string, body: Uint8Array<ArrayBuffer>): Promise<string> {
  const presign = await shareApi.presignSnapshot(fileId, body.byteLength);
  const response = await fetch(presign.upload.uploadUrl, {
    method: 'PUT',
    headers: presign.upload.requiredHeaders,
    body,
  });
  if (!response.ok) throw new Error(`snapshot upload ${response.status}`);
  return presign.key;
}

/**
 * Bản sạch — APM-CMT-e: đủ mọi phần của tài liệu, chỉ bỏ bình luận (luồng + neo) và đề xuất
 * chưa duyệt (như thể đã từ chối — QA 2026-10-13). Link toàn bộ,
 * quyền theo người và "Lưu bản sao" đọc bản này thay cho tệp gốc (người dùng chốt 2026-09-29:
 * luôn bỏ bình luận khi chia sẻ).
 */
export function cleanChilldoc(text: string): string {
  return filterChilldoc(text, DOCUMENT_SHARE_SCOPES);
}

/**
 * Dựng bản sạch từ bản đã lưu `sourceFileId` (`text` = nội dung của nó nếu đang có sẵn, khỏi tải
 * lại), tải lên và đặt cho tài liệu. Trả danh sách link mới (có `cleanCopy`).
 */
export async function uploadCleanCopy(
  fileId: string,
  sourceFileId: string,
  text?: string,
): Promise<DocumentShareLinks> {
  const body = new TextEncoder().encode(cleanChilldoc(text ?? (await savedText(sourceFileId))));
  const key = await putSnapshot(fileId, body);
  const checksumSha256 = await sha256Hex(body.buffer);
  return shareApi.putCleanCopy(fileId, { key, sourceFileId, checksumSha256 });
}

/**
 * Bản sạch khớp bản mới nhất chưa? Chưa ⇒ dựng lại. Gọi **trước** khi tạo link toàn bộ / mời
 * người, để người nhận đầu tiên đã thấy bản không bình luận.
 */
export async function ensureCleanCopy(fileId: string): Promise<void> {
  const links = await shareApi.links(fileId);
  if (links.cleanCopy.fresh) return;
  await uploadCleanCopy(fileId, links.latestFileId);
}

/**
 * Sau khi Lưu một `.chilldoc`: còn link toàn bộ / quyền theo người ⇒ dựng bản sạch từ đúng nội
 * dung vừa lưu. `false` khi tài liệu không được chia sẻ theo lối đó (không làm gì).
 */
export async function refreshCleanCopyAfterSave(fileId: string, text: string): Promise<boolean> {
  const links = await shareApi.links(fileId);
  if (!links.cleanCopy.needed) return false;
  await uploadCleanCopy(fileId, links.latestFileId, text);
  return true;
}
